import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  firstValueFrom,
} from 'rxjs';

import {
  EventTypes,
  OidcSecurityService,
  PublicEventsService,
  type OidcClientNotification,
} from 'angular-auth-oidc-client';

import { APP_CONFIG } from '../config/app-config.model';
import { authorizedHomePath } from '../session/landing-redirect';
import { safeReturnUrl } from '../session/return-url';
import { AuthTelemetry } from './auth-telemetry';
import {
  CLIENT_CONFIG_ID,
  STAFF_CONFIG_ID,
  type IssuerCategory,
} from './claims-mapping';
import { InMemorySecurityStorage } from './in-memory-security-storage';
import { OidcSessionService } from './oidc-session.service';

/**
 * Storage key holding the sessionGuard returnUrl across the authorization
 * redirect round trip (R-110). It is not a credential.
 */
const RETURN_URL_STORAGE_KEY = 'aith.auth.return-url';

/**
 * Storage key holding the category of the in-flight sign-in attempt, so a
 * failed callback whose configuration cannot be identified still records its
 * telemetry against the right issuer category.
 */
const PENDING_CATEGORY_STORAGE_KEY = 'aith.auth.pending-category';

/** Non-sensitive RP-initiated logout state used to restore the denial message. */
const NOT_PROVISIONED_LOGOUT_STATE = 'aith-not-provisioned';

/**
 * Sign-in orchestration (R-105, R-110): per-configuration authorization
 * redirect,
 * bootstrap restoration processing (runs as an APP_INITIALIZER so it completes
 * before the router renders — the FR-010 no-flash guarantee), and session
 * establishment through OidcSessionService's fail-closed derivation.
 *
 * In mockAuth dev mode (or without an auth block) every method is inert —
 * DevSessionService stands in and the OIDC library is never consulted.
 */
@Injectable({ providedIn: 'root' })
export class AuthFlowService {
  private readonly config = inject(APP_CONFIG);
  private readonly oidc = inject(OidcSecurityService);
  private readonly session = inject(OidcSessionService);
  private readonly storage = inject(InMemorySecurityStorage);
  private readonly telemetry = inject(AuthTelemetry);
  private readonly router = inject(Router);
  private readonly oidcEvents = inject(PublicEventsService);
  private readonly derivedIdTokenIssuedAt = new Map<IssuerCategory, number>();
  private readonly signInPendingState = signal(false);
  private interactiveCategory: IssuerCategory | null = null;

  /**
   * The session that owned the issuer when the in-flight silent renewal
   * started. The pinned library fires `SilentRenewStarted` with no payload and
   * `SilentRenewFailed` with only the error, so neither terminal event can be
   * correlated from the event itself — capturing the owner at the one moment
   * it is unambiguous is what keeps a late renewal outcome from being applied
   * to whichever session happens to be current.
   */
  private renewalOwner: { category: IssuerCategory; epoch: number } | null = null;

  /**
   * True while one interactive authorization attempt is in flight. Reset when a
   * bfcache restore proves the redirect was abandoned — see {@link onPageShow}.
   */
  readonly signInPending = this.signInPendingState.asReadonly();

  constructor() {
    const destroyRef = inject(DestroyRef);
    // Silent renewal orchestration (T048): the library's public events drive
    // fresh-identity derivation and fail-closed paths. Inert in mockAuth mode
    // (no configurations exist to emit events).
    this.oidcEvents
      .registerForEvents()
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe((notification) => void this.handleLibraryEvent(notification));

    // A top-level authorization redirect can be abandoned with the browser's
    // Back action. Restored from the back-forward cache the document is not
    // rebuilt, so THIS service instance — with `signInPendingState` still true
    // from the attempt that never returned — is the one the sign-in page reads,
    // and both buttons stay disabled with every retry silently ignored.
    //
    // Returning this way is an abandonment, so the in-flight mirror goes with
    // the flag: nothing will consume that verifier again, and the retry the
    // user is about to make writes a fresh transaction anyway.
    window.addEventListener('pageshow', this.onPageShow);
    destroyRef.onDestroy(() => window.removeEventListener('pageshow', this.onPageShow));
  }

  private readonly onPageShow = (event: PageTransitionEvent): void => {
    if (!event.persisted) return;
    this.signInPendingState.set(false);
    this.storage.discardRedirectMirror();
  };

  /** Whether the given issuer category has a live OIDC configuration. */
  isConfigured(category: IssuerCategory): boolean {
    const auth = this.config.auth;
    if (this.config.mockAuth || auth === null) return false;
    return category === 'staff' || auth.client !== null;
  }

  /**
    * Starts the authorization redirect for one configuration (FR-001). Fails
   * closed: without a matching configuration nothing happens.
   */
  startSignIn(category: IssuerCategory, returnUrl?: string | null): void {
    if (!this.isConfigured(category) || this.signInPendingState()) return;
    if (!this.storage.canUseRedirectMirror()) {
      void this.handleSignInFailure(category, returnUrl);
      return;
    }
    this.signInPendingState.set(true);
    this.interactiveCategory = category;
    let mirrorWritten: boolean;
    if (typeof returnUrl === 'string' && returnUrl !== '') {
      mirrorWritten = this.storage.writeAcrossRedirect(RETURN_URL_STORAGE_KEY, returnUrl);
    } else {
      this.storage.remove(RETURN_URL_STORAGE_KEY);
      mirrorWritten = true;
    }
    mirrorWritten = this.storage.writeAcrossRedirect(PENDING_CATEGORY_STORAGE_KEY, category) && mirrorWritten;
    if (!mirrorWritten) {
      void this.handleSignInFailure(category, returnUrl);
      return;
    }
    const configId = category === 'staff' ? STAFF_CONFIG_ID : CLIENT_CONFIG_ID;
    // Safari blocks windows opened after the library's asynchronous discovery
    // request. A top-level authorization redirect is standards-compliant and
    // lets completeBootstrapCallback process the callback on return.
    this.oidc.authorize(configId);
  }

  /**
   * Processes the OIDC callback (or restores an in-memory session) during
   * bootstrap. Registered by provideAuth() as an APP_INITIALIZER so the
   * callback completes before the router renders any destination.
   *
   * Deny paths (FR-012/FR-019): a not-provisioned derivation ends the
   * provider session and shows the not-provisioned state; a provider
   * error/cancel (or a callback processing failure) returns to /sign-in with
   * a non-technical retry state — provider internals never leave the library
   * boundary.
   */
  async completeBootstrapCallback(): Promise<void> {
    if (this.config.mockAuth || this.config.auth === null) return;
    if (this.consumeNotProvisionedLogoutState()) return;

    // An authorization response belongs to exactly ONE configuration, and the
    // redirect delivers it to the top window where every configuration would
    // otherwise be asked to process it (`checkAuthMultiple`). The other issuer
    // never issued this state, and reaching its discovery endpoint can fail on
    // its own — either way it would report a failure over a sign-in that
    // actually succeeded.
    const pending = this.pendingCategory();

    let responses;
    try {
      responses = pending === null
        ? await firstValueFrom(this.oidc.checkAuthMultiple(window.location.href))
        : [
          await firstValueFrom(
            this.oidc.checkAuth(window.location.href, configIdOf(pending)),
          ),
        ];
    } catch {
      // Callback processing blew up (state mismatch, malformed response…).
      // The raw error stays inside the library boundary (FR-019).
      await this.handleSignInFailure(pending ?? 'staff');
      return;
    }
    for (const response of responses) {
      try {
        await this.handleLoginResponse(response);
      } catch {
        // Same fail-closed boundary as above: this runs as an APP_INITIALIZER,
        // so an escaping rejection (e.g. the ID-token payload read failing
        // after an authenticated callback) would abort bootstrap into a blank
        // page instead of the documented sign-in failure state.
        await this.handleSignInFailure(
          categoryOfConfigId(response.configId) ?? pending ?? 'staff',
        );
      }
    }

    // Bootstrap is the mirror's only consumer, and it has now run. Whatever is
    // left belongs to an authorization that will never be completed — a
    // provider error, a cancellation, or an attempt abandoned before it
    // returned, whose blob the library never got to blank. Without this the
    // verifier, state and nonce stay readable in `sessionStorage` for the rest
    // of the tab's life, which is the difference between FR-015's narrowed
    // exception being attempt-local in fact and only in wording.
    this.storage.discardRedirectMirror();
  }

  private async handleLoginResponse(response: {
    readonly configId?: string;
    readonly isAuthenticated: boolean;
    readonly errorMessage?: string;
  }): Promise<void> {
    const category = categoryOfConfigId(response.configId);
    if (category === null) return;
    if (response.isAuthenticated) {
      try {
        await this.establishSession(category);
      } catch {
        await this.failAuthenticatedCallback(category);
      }
    } else if (response.errorMessage) {
      await this.handleSignInFailure(category);
    }
  }

  /**
   * A callback that authenticated at the provider but could not be turned
   * into a session — typically the ID-token payload read failing.
   *
   * By this point the library has already stored that configuration's new
   * credentials, so showing the retry state alone would leave the previous
   * session authenticated locally while every token read for its category
   * returns the replacement's token. Fail closed: end that owner and discard
   * the half-established provider state before the retry state renders.
   */
  private async failAuthenticatedCallback(category: IssuerCategory): Promise<void> {
    const configId = category === 'staff' ? STAFF_CONFIG_ID : CLIENT_CONFIG_ID;
    // Only the owner of this category is invalidated — a session established
    // through the *other* issuer never shared the storage that just changed.
    if (this.session.issuerCategory() === category) {
      this.session.endSession();
    }
    this.derivedIdTokenIssuedAt.delete(category);
    this.oidc.logoffLocal(configId);
    this.storage.clearConfiguration(configId);
    await this.handleSignInFailure(category);
  }

  /**
   * Derives the session from the validated ID-token claims and, on success,
   * records the signIn event and lands the user on their original destination
   * (or their role's authorized home). A not-provisioned derivation (FR-012)
   * ends the provider session for that configuration, clears its in-memory
   * storage, records the denial event, and routes to the sign-in page's
   * not-provisioned state — no workspace content has rendered.
   */
  private async establishSession(category: IssuerCategory): Promise<void> {
    const configId = category === 'staff' ? STAFF_CONFIG_ID : CLIENT_CONFIG_ID;
    const payload: unknown = await firstValueFrom(
      this.oidc.getPayloadFromIdToken(false, configId),
    );
    const claims = isRecord(payload) ? payload : {};
    const issuedAt = idTokenIssuedAt(claims);
    const derivation = this.session.applyValidatedClaims(category, claims, 'signIn');

    if (derivation.kind === 'notProvisioned') {
      this.derivedIdTokenIssuedAt.delete(category);
      this.storage.remove(PENDING_CATEGORY_STORAGE_KEY);
      this.telemetry.denial(category);
      // `applyValidatedClaims` already ended the local session on a
      // not-provisioned derivation. The epoch is the sole ownership signal for
      // `stillOwnedBy`/`throwIfOwnerChanged`/`guardEpoch`, so it must advance
      // exactly once per real transition — ending again here would double it.
      const deniedEpoch = this.session.sessionEpoch();
      await this.endProviderSession(configId, NOT_PROVISIONED_LOGOUT_STATE, deniedEpoch);
      if (this.session.sessionEpoch() !== deniedEpoch) return;
      // Same ordering as handleSignInFailure: the returnUrl is read out of the
      // mirror before the mirror is dropped.
      const queryParams = this.messageQueryParams('not-provisioned');
      this.storage.discardRedirectMirror();
      await this.router.navigate(['/sign-in'], { queryParams });
      return;
    }

    if (issuedAt !== null) this.derivedIdTokenIssuedAt.set(category, issuedAt);
    // FR-003 / concurrent-sessions edge case: the most recently established
    // session wins — the other configuration is locally signed out and its
    // in-memory state discarded, deterministically.
    this.discardOtherConfiguration(category);
    this.storage.remove(PENDING_CATEGORY_STORAGE_KEY);
    this.telemetry.signIn(category);
    const target = this.consumeReturnUrl() ?? authorizedHomePath(derivation.session.roles);
    await this.router.navigateByUrl(target);
  }

  /** Local sign-out + storage clear for the configuration that just lost (never the winner). */
  private discardOtherConfiguration(winner: IssuerCategory): void {
    const otherId = winner === 'staff' ? CLIENT_CONFIG_ID : STAFF_CONFIG_ID;
    this.oidc.logoffLocal(otherId);
    this.storage.clearConfiguration(otherId);
    this.derivedIdTokenIssuedAt.delete(winner === 'staff' ? 'client' : 'staff');
  }

  /**
   * Explicit dual-issuer sign-out (FR-014): records the signOut event, ends
    * the local session immediately, lets provider revocation/end-session consume
    * its stored inputs, then guarantees every in-memory credential artifact is
    * cleared and lands on /sign-in. No usable credential remains afterwards.
   */
  signOut(): void {
    const category = this.session.issuerCategory();
    if (category === null || !this.session.isAuthenticated()) return;
    const configId = category === 'staff' ? STAFF_CONFIG_ID : CLIENT_CONFIG_ID;

    this.telemetry.signOut(category);
    this.session.endSession();
    this.derivedIdTokenIssuedAt.clear();
    this.renewalOwner = null;
    // Captured after endSession: the continuation must be able to tell that a
    // replacement session was established while the provider round trip below
    // was in flight, and leave that replacement's state alone.
    void this.finishSignOut(configId, this.session.sessionEpoch());
  }

  /**
   * Completes sign-out after the provider round trip. That await is unbounded
   * (a network call to the authority's revoke/logout endpoints) and the local
   * session is already ended, so `/sign-in` is reachable and a replacement
   * session can be fully established before this resumes. The global cleanup
   * and the redirect below would otherwise erase that replacement's
   * credentials and navigate it away while its local session stayed
   * authenticated — an authenticated shell with no usable credential.
   */
  private async finishSignOut(configId: string, epoch: number): Promise<void> {
    await this.endProviderSession(configId, undefined, epoch);
    if (this.session.sessionEpoch() !== epoch) return;
    this.storage.clear();
    await this.router.navigateByUrl('/sign-in');
  }

  private async handleLibraryEvent(notification: OidcClientNotification<unknown>): Promise<void> {
    if (this.config.mockAuth || this.config.auth === null) return;
    if (notification.type === EventTypes.SilentRenewStarted) {
      this.captureRenewalOwner();
    } else if (notification.type === EventTypes.ConfigLoadingFailed && this.signInPendingState()) {
      const category = this.interactiveCategory;
      if (category !== null) await this.handleSignInFailure(category);
    } else if (notification.type === EventTypes.NewAuthenticationResult) {
      await this.handleRenewalSuccess(notification.value);
    } else if (notification.type === EventTypes.SilentRenewFailed) {
      await this.handleTerminalRenewalFailure();
    }
  }

  /** Records who owns the issuer at the only point the renewal is unambiguous. */
  private captureRenewalOwner(): void {
    const category = this.session.issuerCategory();
    this.renewalOwner = category === null || !this.session.isAuthenticated()
      ? null
      : { category, epoch: this.session.sessionEpoch() };
  }

  /**
   * Whether the captured owner of a renewal still owns the issuer. A renewal
   * derivation deliberately does not advance the epoch (R-105), so following a
   * renewal keeps ownership; a sign-out, a fail-closed teardown, or any
   * replacement sign-in — including one through the same issuer — does advance
   * it and makes the captured owner stale.
   */
  private stillOwnedBy(owner: { category: IssuerCategory; epoch: number }): boolean {
    return this.session.sessionEpoch() === owner.epoch
      && this.session.issuerCategory() === owner.category
      && this.session.isAuthenticated();
  }

  /**
   * A silent renewal produced a fresh validated ID token (R-105): derivation
   * re-runs and a role change takes effect immediately. Missing/reused claims
   * fail closed; a freshly derived not-provisioned result runs FR-012.
   */
  private async handleRenewalSuccess(value: unknown): Promise<void> {
    if (!isRecord(value) || value['isRenewProcess'] !== true || value['isAuthenticated'] !== true) {
      return;
    }
    const category = categoryOfConfigId(value['configId'] as string | undefined);
    if (category === null) return;
    // The renewal must belong to the owner that started it. Matching only the
    // category would let a late result from a previous owner re-derive (or
    // tear down) a replacement session that reached the same issuer.
    const owner = this.renewalOwner;
    if (owner === null || owner.category !== category || !this.stillOwnedBy(owner)) return;
    this.renewalOwner = null;

    let payload: unknown;
    try {
      payload = await firstValueFrom(
        this.oidc.getPayloadFromIdToken(false, category),
      );
    } catch {
      // The library reported a successful renewal but could not produce the
      // renewed ID-token payload. The event subscription is fire-and-forget,
      // so an escaping rejection would leave the old session authenticated
      // with no telemetry. Fail closed — but only if the captured owner still
      // owns the issuer; a session that ended or was replaced during the
      // await is not ours to tear down.
      if (this.stillOwnedBy(owner)) {
        await this.failRenewal(category, true);
      }
      return;
    }
    if (!this.stillOwnedBy(owner)) return;

    const claims = isRecord(payload) ? payload : {};
    const issuedAt = idTokenIssuedAt(claims);
    if (issuedAt === null || issuedAt === this.derivedIdTokenIssuedAt.get(category)) {
      await this.failRenewal(category, true);
      return;
    }

    const derivation = this.session.applyValidatedClaims(category, claims, 'renewal');
    if (derivation.kind === 'session') {
      this.derivedIdTokenIssuedAt.set(category, issuedAt);
      this.telemetry.renewal(category, 'success');
      return;
    }

    const configId = category === 'staff' ? STAFF_CONFIG_ID : CLIENT_CONFIG_ID;
    this.derivedIdTokenIssuedAt.delete(category);
    this.telemetry.denial(category);
    // Ended by `applyValidatedClaims` above — see the note in establishSession:
    // the epoch advances once per real transition, never twice.
    const deniedEpoch = this.session.sessionEpoch();
    await this.endProviderSession(configId, NOT_PROVISIONED_LOGOUT_STATE, deniedEpoch);
    if (this.session.sessionEpoch() !== deniedEpoch) return;
    await this.router.navigate(['/sign-in'], {
      queryParams: this.messageQueryParams('not-provisioned'),
    });
  }

  /**
   * Terminal renewal failure (FR-013): one renewal failure event (once per
   * terminal failure — after the session ends, later retry signals are
   * inert), the local session ends, and the user is redirected to /sign-in
   * with the current destination preserved. In-flight data requests surface
   * the app's normal error presentation via the token provider's fail-closed
   * rejection (no blank page, no endless spinner).
   */
  private async handleTerminalRenewalFailure(): Promise<void> {
    // `SilentRenewFailed` carries only the error — no configId — so the owner
    // captured at SilentRenewStarted is the only sound attribution. Without
    // one, or once it is stale, the failure belongs to a session that no
    // longer exists and must not end whichever session is current.
    const owner = this.renewalOwner;
    if (owner === null) return;
    this.renewalOwner = null;
    if (!this.stillOwnedBy(owner)) return;

    await this.failRenewal(owner.category, false);
  }

  private async failRenewal(category: IssuerCategory, clearProviderState: boolean): Promise<void> {
    this.telemetry.renewal(category, 'failure');
    this.session.endSession();
    this.derivedIdTokenIssuedAt.delete(category);
    if (clearProviderState) {
      const configId = category === 'staff' ? STAFF_CONFIG_ID : CLIENT_CONFIG_ID;
      this.oidc.logoffLocal(configId);
      this.storage.clearConfiguration(configId);
    }
    const currentUrl = this.router.url;
    await this.router.navigate(['/sign-in'], {
      queryParams: currentUrl.startsWith('/sign-in') ? {} : { returnUrl: currentUrl },
    });
  }

  /**
   * Provider-side failure or cancellation (FR-019): return to /sign-in with
   * the non-technical retry state; the intended destination stays preserved
   * for the retry and the signInFailure event carries no internals.
   */
  private async handleSignInFailure(
    category: IssuerCategory,
    fallbackReturnUrl?: string | null,
  ): Promise<void> {
    this.signInPendingState.set(false);
    this.interactiveCategory = null;
    this.telemetry.signInFailure(category);
    // Read the preserved returnUrl BEFORE the mirror goes: it is carried into
    // the retry through the address bar, not through storage.
    const queryParams = this.messageQueryParams('sign-in-failed', fallbackReturnUrl);
    this.storage.discardRedirectMirror();
    await this.router.navigate(['/sign-in'], { queryParams });
  }

  /**
   * The failure-state query params, keeping any preserved returnUrl for the
   * retry. Validated here too: this path reflects the value back into the
   * address bar, so an unusable one is dropped rather than round-tripped.
   */
  private messageQueryParams(
    authMessage: 'not-provisioned' | 'sign-in-failed',
    fallbackReturnUrl?: string | null,
  ): Record<string, string> {
    const returnUrl = safeReturnUrl(this.storage.read(RETURN_URL_STORAGE_KEY))
      ?? safeReturnUrl(fallbackReturnUrl ?? null);
    return returnUrl === null ? { authMessage } : { authMessage, returnUrl };
  }

  private pendingCategory(): IssuerCategory | null {
    const pending = this.storage.read(PENDING_CATEGORY_STORAGE_KEY);
    return pending === 'staff' || pending === 'client' ? pending : null;
  }

  private consumeReturnUrl(): string | null {
    const returnUrl = this.storage.read(RETURN_URL_STORAGE_KEY);
    this.storage.remove(RETURN_URL_STORAGE_KEY);
    return safeReturnUrl(returnUrl);
  }

  /**
   * Ends the provider-side session and discards that configuration's stored
   * state. `guardEpoch`, when given, is the session epoch captured before the
   * round trip: the cleanup is skipped if a replacement session took over
   * while it was in flight, so this never erases credentials it did not
   * establish.
   */
  private async endProviderSession(
    configId: string,
    state?: string,
    guardEpoch?: number,
  ): Promise<void> {
    const logoutOptions = state === undefined ? undefined : { customParams: { state } };
    try {
      await firstValueFrom(this.oidc.logoffAndRevokeTokens(configId, logoutOptions));
    } catch {
      try {
        await firstValueFrom(this.oidc.logoff(configId, logoutOptions));
      } catch {
        // Local fail-closed cleanup must not depend on provider availability.
      }
    } finally {
      if (guardEpoch === undefined || this.session.sessionEpoch() === guardEpoch) {
        this.storage.clearConfiguration(configId);
      }
    }
  }

  private consumeNotProvisionedLogoutState(): boolean {
    const url = new URL(window.location.href);
    if (url.searchParams.get('state') !== NOT_PROVISIONED_LOGOUT_STATE) return false;
    const returnUrl = safeReturnUrl(this.storage.read(RETURN_URL_STORAGE_KEY));
    this.storage.remove(RETURN_URL_STORAGE_KEY);
    this.storage.remove(PENDING_CATEGORY_STORAGE_KEY);
    this.storage.discardRedirectMirror();
    url.searchParams.delete('state');
    url.searchParams.set('authMessage', 'not-provisioned');
    if (returnUrl !== null) url.searchParams.set('returnUrl', returnUrl);
    window.history.replaceState(window.history.state, '', url);
    return true;
  }
}

function categoryOfConfigId(configId: string | undefined): IssuerCategory | null {
  if (configId === STAFF_CONFIG_ID) return 'staff';
  if (configId === CLIENT_CONFIG_ID) return 'client';
  return null;
}

function configIdOf(category: IssuerCategory): string {
  return category === 'staff' ? STAFF_CONFIG_ID : CLIENT_CONFIG_ID;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function idTokenIssuedAt(claims: Record<string, unknown>): number | null {
  const issuedAt = claims['iat'];
  return typeof issuedAt === 'number' && Number.isFinite(issuedAt) ? issuedAt : null;
}
